/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { AbsenceOwner } from '@absences/store/absence-owner';
import { ApiClient } from '@shared/api-client';
import { Absence, AbsenceClass, AbsenceSummaryEntry } from '@shared/models';
import { AbsenceWrite } from '@shared/models/absence-write';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AbsenceService extends ApiClient {
  constructor(private httpClient: HttpClient, private datePipe: DatePipe) {
    super();
  }

  getAbsences(params: {driver?: string, vehicle?: string, from?: any, till?: any, targetEnabled?: boolean} = {}): Observable<Absence[]> {
    if (params.driver) {
      params.driver = this.extractId(params.driver);
    }
    if (params.vehicle) {
      params.vehicle = this.extractId(params.vehicle);
    }
    if (params.from) {
      params.from = this.datePipe.transform(params.from, 'yyyy-MM-dd');
    }
    if (params.till) {
      params.till = this.datePipe.transform(params.till, 'yyyy-MM-dd');
    }
    return this.httpClient.get<Absence[]>(`${environment.backendServer}/api/absences`, {
      headers: this.LD_JSON_HEADERS,
      params
    }).pipe(
      map(response => response['hydra:member'])
    );
  }

  getAbsenceSummary(date: any): Observable<AbsenceSummaryEntry[]> {
    return this.httpClient.get<AbsenceSummaryEntry[]>(`${environment.backendServer}/api/absences/summary`, {
      headers: this.LD_JSON_HEADERS,
      params: { date: this.datePipe.transform(date, 'yyyy-MM-dd') }
    });
  }

  getAbsenceClasses(target?: AbsenceOwner['property']): Observable<AbsenceClass[]> {
    return this.httpClient.get<AbsenceClass[]>(`${environment.backendServer}/api/absences/meta`, {
      headers: this.LD_JSON_HEADERS,
      params: target ? { target } : {}
    });
  }

  createAbsence(absence: AbsenceWrite): Observable<Absence> {
    absence.startDate = this.datePipe.transform(absence.startDate, 'yyyy-MM-dd');
    absence.endDate = this.datePipe.transform(absence.endDate, 'yyyy-MM-dd');
    return this.httpClient.post<Absence>(`${environment.backendServer}/api/absences`, absence, {
      headers: this.LD_JSON_HEADERS,
      observe: 'response'
    }).pipe(
      map(response => response.body)
    );
  }

  updateAbsence(absence: AbsenceWrite): Observable<Absence> {
    absence.startDate = this.datePipe.transform(absence.startDate, 'yyyy-MM-dd');
    absence.endDate = this.datePipe.transform(absence.endDate, 'yyyy-MM-dd');
    return this.httpClient.put<Absence>(`${environment.backendServer}/api/absences/${this.extractId(absence['@id'])}`, absence, {
      headers: this.LD_JSON_HEADERS,
      observe: 'response'
    }).pipe(
      map(response => response.body)
    );
  }

  deleteAbsence(absenceId: string): Observable<any> {
    return this.httpClient.delete(`${environment.backendServer}/api/absences/${this.extractId(absenceId)}`);
  }
}
